import { useEffect, useState } from "react";
import axios from "axios";
import { Sprout, LogOut, RefreshCw, Inbox, Users, ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const authConfig = () => {
  const token = localStorage.getItem("mft_admin_token");
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };
};

export default function Admin() {
  const [auth, setAuth] = useState(null); // null = checking, false = logged out, object = admin
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [contacts, setContacts] = useState([]);

  const loadData = async () => {
    const [w, c] = await Promise.all([
      axios.get(`${API}/admin/waitlist`, authConfig()),
      axios.get(`${API}/admin/contacts`, authConfig()),
    ]);
    setWaitlist(w.data.items);
    setContacts(c.data.items);
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, authConfig())
      .then((r) => {
        setAuth(r.data);
        loadData().catch(() => toast.error("Couldn't load data."));
      })
      .catch(() => setAuth(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, authConfig());
      if (data.token) localStorage.setItem("mft_admin_token", data.token);
      setAuth(data);
      toast.success("Welcome back.");
      loadData().catch(() => toast.error("Couldn't load data."));
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, authConfig()).catch(() => {});
    localStorage.removeItem("mft_admin_token");
    setAuth(false);
    setWaitlist([]);
    setContacts([]);
  };

  const downloadWaitlistCsv = () => {
    const escapeCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [
      ["Email", "Signed up"],
      ...waitlist.map((w) => [w.email, fmtDate(w.created_at)]),
    ];
    const csv = rows.map((r) => r.map(escapeCell).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "my-fresh-tracker-waitlist.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${waitlist.length} signup${waitlist.length === 1 ? "" : "s"}.`);
  };

  if (auth === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <p className="uppercase tracking-[0.2em] text-xs text-[#111111]/50">Checking session…</p>
      </div>
    );
  }

  if (auth === false) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login">
        <div className="w-full max-w-md">
          <a
            href="/"
            data-testid="admin-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#111111]/50 hover:text-[#1F9D55] transition-colors duration-300"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
          </a>
          <div className="mt-8 flex items-center gap-3">
            <span className="h-10 w-10 rounded-full bg-[#1F9D55] flex items-center justify-center">
              <Sprout className="h-5 w-5 text-[#F7F6F2]" />
            </span>
            <h1 className="font-serif text-4xl tracking-tight">
              Admin <em className="text-[#1F9D55]">sign in</em>
            </h1>
          </div>
          <form onSubmit={login} className="mt-10 space-y-8" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="uppercase tracking-[0.2em] text-xs font-semibold text-[#111111]/50">
                Email
              </label>
              <input
                id="admin-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="admin-email-input"
                className="w-full bg-transparent outline-none border-b border-[#111111]/25 focus:border-[#1F9D55] transition-colors duration-300 py-4 text-lg placeholder:text-[#111111]/35"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="uppercase tracking-[0.2em] text-xs font-semibold text-[#111111]/50">
                Password
              </label>
              <input
                id="admin-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="admin-password-input"
                className="w-full bg-transparent outline-none border-b border-[#111111]/25 focus:border-[#1F9D55] transition-colors duration-300 py-4 text-lg placeholder:text-[#111111]/35"
              />
            </div>
            {error && (
              <p className="text-sm font-medium text-[#16783F]" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="rounded-full bg-[#111111] text-[#F7F6F2] px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#1F9D55] transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Signing in" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 md:px-12 py-10" data-testid="admin-dashboard">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between border-b border-[#111111]/10 pb-6">
          <div className="flex items-center gap-3">
            <span className="h-9 w-9 rounded-full bg-[#1F9D55] flex items-center justify-center">
              <Sprout className="h-4 w-4 text-[#F7F6F2]" />
            </span>
            <div>
              <h1 className="font-serif text-2xl md:text-3xl tracking-tight">The back kitchen</h1>
              <p className="font-mono text-xs text-[#111111]/50">{auth.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => loadData().then(() => toast.success("Refreshed.")).catch(() => toast.error("Refresh failed."))}
              data-testid="admin-refresh-button"
              className="rounded-full border border-[#111111]/20 px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold hover:border-[#1F9D55] transition-colors duration-300 flex items-center gap-2"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="rounded-full bg-[#111111] text-[#F7F6F2] px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#1F9D55] transition-colors duration-300 flex items-center gap-2"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 mt-14">
          <section data-testid="admin-waitlist-panel">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-[#1F9D55]" />
              <h2 className="font-serif text-3xl tracking-tight">Waitlist</h2>
              <span className="font-mono text-xs bg-[#F2C94C] text-[#0A130B] px-3 py-1 rounded-full" data-testid="admin-waitlist-count">
                {waitlist.length}
              </span>
              <button
                onClick={downloadWaitlistCsv}
                disabled={waitlist.length === 0}
                data-testid="admin-waitlist-csv-button"
                className="ml-auto rounded-full border border-[#111111]/20 px-4 py-2 text-xs uppercase tracking-[0.2em] font-semibold hover:border-[#1F9D55] transition-colors duration-300 flex items-center gap-2 disabled:opacity-40"
              >
                <Download className="h-3.5 w-3.5" /> CSV
              </button>
            </div>
            <div className="mt-8 border-t border-[#111111]/10">
              {waitlist.length === 0 && (
                <p className="py-8 text-sm text-[#111111]/50" data-testid="admin-waitlist-empty">
                  No signups yet.
                </p>
              )}
              {waitlist.map((w) => (
                <div key={w.id} className="flex items-baseline justify-between gap-6 py-4 border-b border-[#111111]/10" data-testid={`waitlist-row-${w.id}`}>
                  <span className="font-mono text-sm">{w.email}</span>
                  <span className="text-xs text-[#111111]/50 shrink-0">{fmtDate(w.created_at)}</span>
                </div>
              ))}
            </div>
          </section>

          <section data-testid="admin-contacts-panel">
            <div className="flex items-center gap-3">
              <Inbox className="h-5 w-5 text-[#1F9D55]" />
              <h2 className="font-serif text-3xl tracking-tight">Support messages</h2>
              <span className="font-mono text-xs bg-[#F2C94C] text-[#0A130B] px-3 py-1 rounded-full" data-testid="admin-contacts-count">
                {contacts.length}
              </span>
            </div>
            <div className="mt-8 space-y-6">
              {contacts.length === 0 && (
                <p className="py-8 text-sm text-[#111111]/50 border-t border-[#111111]/10" data-testid="admin-contacts-empty">
                  No messages yet.
                </p>
              )}
              {contacts.map((c) => (
                <div key={c.id} className="border border-[#111111]/10 p-6" data-testid={`contact-row-${c.id}`}>
                  <div className="flex items-baseline justify-between gap-4">
                    <p className="font-medium">
                      {c.name} <span className="font-mono text-xs text-[#111111]/50 ml-2">{c.email}</span>
                    </p>
                    <span className="text-xs text-[#111111]/50 shrink-0">{fmtDate(c.created_at)}</span>
                  </div>
                  <p className="mt-3 text-sm text-[#111111]/70 leading-relaxed whitespace-pre-wrap">{c.message}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
